import type {
  DeckAssessment,
  DppFinderResult,
  NotesCdnFileDetails,
  NotesFileDetails,
} from '../interface/data/DeckAssessment';

export type DeckAssessmentDotStatus =
  | 'not_applicable'
  | 'pending'
  | 'in_progress'
  | 'failed'
  | 'ok'
  | 'needs_fix';

const SUBTOPIC_STATUS = {
  SCHEDULED: 'scheduled',
  PROCESSING: 'processing',
  DONE: 'done',
  FAILED: 'failed',
} as const;

/** PS Foundation (Pathshala) covers do not include a faculty headshot. */
export function isPsFoundationCenter(center?: string): boolean {
  const name = center?.trim() ?? '';
  return /pathshala/i.test(name);
}

function hasPresentText(value?: string): boolean {
  return Boolean(value?.trim());
}

function hasPresentSubtopics(subtopics?: string[]): boolean {
  return Boolean(
    subtopics?.some(
      (item) => typeof item === 'string' && item.trim().length > 0,
    ),
  );
}

export function hasNotesExtractionGaps(
  fileDetails?: NotesFileDetails,
): boolean {
  if (!fileDetails) return false;

  return (
    !hasPresentText(fileDetails.chapterName) ||
    !hasPresentText(fileDetails.lectureNo) ||
    !hasPresentSubtopics(fileDetails.subtopics)
  );
}

export function buildCdnFileUrl(
  details?: NotesCdnFileDetails,
): string | undefined {
  if (!details?.baseUrl || !details?.key) return undefined;
  return `${details.baseUrl}${details.key}`;
}

export function getUpdatedNotesPdfUrl(
  fileDetails?: NotesFileDetails,
): string | undefined {
  return buildCdnFileUrl(fileDetails?.updatedFileDetails);
}

export function getOriginalNotesPdfUrl(
  fileDetails?: NotesFileDetails,
): string | undefined {
  return buildCdnFileUrl(fileDetails);
}

export type DeckAssessmentIssue = {
  id: string;
  message: string;
  /** Pipeline can address this in the updated PDF. */
  fixableInUpdatedPdf: boolean;
  /** True when fixable and the updated PDF was produced. */
  fixedInUpdatedPdf: boolean;
};

export type DppIdentificationIssue = {
  id: string;
  message: string;
};

export type DppIdentificationSummary = {
  issueCount: number;
  issues: DppIdentificationIssue[];
  statusLabel: string;
  statusTone: 'ok' | 'warning';
};

export type DeckAssessmentSummary = {
  issueCount: number;
  issues: DeckAssessmentIssue[];
  fixedInUpdatedPdfCount: number;
  notFixedInUpdatedPdfCount: number;
  statusLabel: string;
  statusTone: 'ok' | 'fixed' | 'partial' | 'warning';
};

function buildIssue(
  id: string,
  message: string,
  fixableInUpdatedPdf: boolean,
  fixedInUpdatedPdf: boolean,
): DeckAssessmentIssue {
  return {
    id,
    message,
    fixableInUpdatedPdf,
    fixedInUpdatedPdf: fixableInUpdatedPdf && fixedInUpdatedPdf,
  };
}

export function buildDeckAssessmentIssues(
  fileDetails?: NotesFileDetails,
  center?: string,
): DeckAssessmentIssue[] {
  const assessment = fileDetails?.deckAssessment;
  if (!assessment) return [];

  const issues: DeckAssessmentIssue[] = [];
  const hasUpdatedPdf = Boolean(getUpdatedNotesPdfUrl(fileDetails));
  const deckFixed = Boolean(fileDetails.deckNeedsFixes) && hasUpdatedPdf;
  const chapterCoverFixed =
    Boolean(fileDetails.needsChapterNameWrite) && hasUpdatedPdf;
  const lectureCoverFixed =
    Boolean(fileDetails.needsLectureNoWrite) && hasUpdatedPdf;

  if (assessment.blankSlideNumbers?.length) {
    issues.push(
      buildIssue(
        'blank-slides',
        `${assessment.blankSlideNumbers.length} blank slide(s)`,
        true,
        deckFixed,
      ),
    );
  }
  if (assessment.coverSlideNumber == null) {
    issues.push(
      buildIssue('cover-missing', 'Cover slide not found', false, false),
    );
  } else if (!assessment.isCoverFirstSlide) {
    issues.push(
      buildIssue('cover-first', 'Cover not on slide 1', true, deckFixed),
    );
  }
  if (assessment.todaysGoalSlideNumber == null) {
    issues.push(
      buildIssue(
        'todays-goal-missing',
        "Today's goal slide not found",
        false,
        false,
      ),
    );
  } else if (!assessment.isTodaysGoalSecondSlide) {
    issues.push(
      buildIssue(
        'todays-goal-second',
        "Today's goal not on slide 2",
        true,
        deckFixed,
      ),
    );
  }
  if (assessment.thankYouSlideNumbers?.length > 1) {
    issues.push(
      buildIssue(
        'thank-you-duplicate',
        `${assessment.thankYouSlideNumbers.length} thank-you slide(s)`,
        true,
        deckFixed,
      ),
    );
  }
  if (
    assessment.thankYouSlideNumbers?.length === 0 ||
    !assessment.isThankYouLastSlide
  ) {
    issues.push(
      buildIssue(
        'thank-you-last',
        assessment.thankYouSlideNumbers?.length === 0
          ? 'Thank-you slide missing'
          : 'Thank-you not last slide',
        true,
        deckFixed,
      ),
    );
  }
  if (
    assessment.awarenessSlideNumbers?.length > 0 &&
    !assessment.isAwarenessSlidesBeforeThankYou
  ) {
    issues.push(
      buildIssue(
        'awareness-slides',
        `${assessment.awarenessSlideNumbers.length} awareness slide(s) not before thank-you`,
        true,
        deckFixed,
      ),
    );
  }
  if (assessment.slidesMissingPwLogo?.length) {
    issues.push(
      buildIssue(
        'pw-logo',
        `${assessment.slidesMissingPwLogo.length} slide(s) missing PW logo`,
        false,
        false,
      ),
    );
  }
  if (
    !isPsFoundationCenter(center) &&
    assessment.coverSlideNumber != null &&
    !assessment.hasFacultyProfileOnCoverSlide
  ) {
    issues.push(
      buildIssue(
        'faculty-profile',
        'Faculty profile missing on cover',
        false,
        false,
      ),
    );
  }

  const chapterName = fileDetails.chapterName?.trim();
  const lectureNo = fileDetails.lectureNo?.trim();

  if (!chapterName) {
    issues.push(
      buildIssue('chapter-missing', 'Chapter name not found', false, false),
    );
  } else if (fileDetails.needsChapterNameWrite) {
    issues.push(
      buildIssue(
        'chapter-cover',
        `Chapter name missing on cover — written as "${chapterName}"`,
        true,
        chapterCoverFixed,
      ),
    );
  }

  if (!lectureNo) {
    issues.push(
      buildIssue('lecture-missing', 'Lecture number not found', false, false),
    );
  } else if (fileDetails.needsLectureNoWrite) {
    issues.push(
      buildIssue(
        'lecture-cover',
        `Lecture number missing on cover — written as "${lectureNo}"`,
        true,
        lectureCoverFixed,
      ),
    );
  }

  if (!hasPresentSubtopics(fileDetails.subtopics)) {
    issues.push(
      buildIssue('subtopics-missing', 'Subtopics not found', false, false),
    );
  } else if (fileDetails.subtopicsFromContent) {
    issues.push(
      buildIssue(
        'subtopics-from-content',
        "Subtopics inferred from content slides — Today's Goal written in updated PDF",
        true,
        deckFixed,
      ),
    );
  }

  return issues;
}

export function summarizeDeckAssessment(
  fileDetails?: NotesFileDetails,
  center?: string,
): DeckAssessmentSummary {
  const issues = buildDeckAssessmentIssues(fileDetails, center);

  if (issues.length === 0) {
    return {
      issueCount: 0,
      issues,
      fixedInUpdatedPdfCount: 0,
      notFixedInUpdatedPdfCount: 0,
      statusLabel: 'Deck OK',
      statusTone: 'ok',
    };
  }

  const fixedInUpdatedPdfCount = issues.filter(
    (issue) => issue.fixedInUpdatedPdf,
  ).length;
  const notFixedInUpdatedPdfCount = issues.length - fixedInUpdatedPdfCount;
  const hasUpdatedPdf = Boolean(getUpdatedNotesPdfUrl(fileDetails));

  if (hasUpdatedPdf && fixedInUpdatedPdfCount > 0) {
    const statusTone = notFixedInUpdatedPdfCount === 0 ? 'fixed' : 'partial';

    return {
      issueCount: issues.length,
      issues,
      fixedInUpdatedPdfCount,
      notFixedInUpdatedPdfCount,
      statusLabel:
        notFixedInUpdatedPdfCount === 0
          ? `All ${issues.length} issue${issues.length === 1 ? '' : 's'} fixed in updated PDF`
          : `${fixedInUpdatedPdfCount} fixed in updated PDF · ${notFixedInUpdatedPdfCount} not auto-fixed`,
      statusTone,
    };
  }

  return {
    issueCount: issues.length,
    issues,
    fixedInUpdatedPdfCount,
    notFixedInUpdatedPdfCount,
    statusLabel: `${issues.length} issue${issues.length === 1 ? '' : 's'}`,
    statusTone: 'warning',
  };
}

export function resolveDeckAssessmentDotStatus(
  fileType: string,
  fileDetails?: NotesFileDetails,
  center?: string,
): DeckAssessmentDotStatus {
  if (fileType !== 'notes') {
    return 'not_applicable';
  }

  const hasAssessmentData =
    Boolean(fileDetails?.deckAssessment) ||
    Boolean(fileDetails?.dppIdentification);

  if (hasAssessmentData) {
    const deckHasIssues = fileDetails?.deckAssessment
      ? summarizeDeckAssessment(fileDetails, center).issueCount > 0
      : false;
    const dppHasIssues = dppIdentificationHasIssues(
      fileDetails?.dppIdentification,
    );

    return deckHasIssues || dppHasIssues ? 'needs_fix' : 'ok';
  }

  const subtopicStatus = fileDetails?.subtopicStatus?.trim().toLowerCase();

  if (subtopicStatus === SUBTOPIC_STATUS.FAILED) {
    return 'failed';
  }

  if (
    subtopicStatus === SUBTOPIC_STATUS.PROCESSING ||
    subtopicStatus === SUBTOPIC_STATUS.SCHEDULED
  ) {
    return 'in_progress';
  }

  if (subtopicStatus === SUBTOPIC_STATUS.DONE) {
    return 'in_progress';
  }

  return 'pending';
}

export function getDeckAssessmentDotTooltip(
  fileDetails?: NotesFileDetails,
  center?: string,
): string {
  const hasAssessmentData =
    Boolean(fileDetails?.deckAssessment) ||
    Boolean(fileDetails?.dppIdentification);

  if (!hasAssessmentData) {
    const subtopicStatus = fileDetails?.subtopicStatus?.trim().toLowerCase();

    if (subtopicStatus === SUBTOPIC_STATUS.FAILED) {
      return fileDetails?.subtopicFailureReason?.trim() || 'OCR failed. Check video file for details.';
    }

    if (subtopicStatus === SUBTOPIC_STATUS.SCHEDULED) {
      return 'OCR scheduled';
    }

    if (
      subtopicStatus === SUBTOPIC_STATUS.PROCESSING ||
      subtopicStatus === SUBTOPIC_STATUS.DONE
    ) {
      return 'OCR in progress';
    }

    return 'OCR pending. Check video file for details.';
  }

  const parts: string[] = [];

  if (fileDetails?.deckAssessment) {
    const summary = summarizeDeckAssessment(fileDetails, center);

    if (summary.issueCount === 0) {
      parts.push('Deck OK — no issues');
    } else {
      const issueLabels = summary.issues.map((issue) =>
        issue.fixedInUpdatedPdf ? `${issue.message} (fixed)` : issue.message,
      );
      parts.push(`${issueLabels.join(' · ')}`);
    }
  }

  const dppSummary = summarizeDppIdentificationTooltip(
    fileDetails?.dppIdentification,
  );
  if (dppSummary) {
    parts.push(dppSummary);
  }

  if (parts.length === 0) {
    return 'Deck assessment not available';
  }

  return `${parts.join(' · ')} — click for details`;
}

export function formatDeckAssessmentRows(
  assessment: DeckAssessment,
  center?: string,
): Array<{ label: string; value: string }> {
  const rows: Array<{ label: string; value: string }> = [
    {
      label: 'Cover first slide',
      value:
        assessment.coverSlideNumber == null
          ? '—'
          : assessment.isCoverFirstSlide
            ? 'Yes'
            : 'No',
    },
    {
      label: "Today's goal second",
      value:
        assessment.todaysGoalSlideNumber == null
          ? '-'
          : assessment.isTodaysGoalSecondSlide
            ? 'Yes'
            : 'No',
    },
    {
      label: 'Duplicate Thank-you slide(s)',
      value:
        assessment.thankYouSlideNumbers?.length > 1
          ? assessment.thankYouSlideNumbers.join(', ')
          : 'None',
    },
    {
      label: 'Thank-you last slide',
      value: assessment.isThankYouLastSlide ? 'Yes' : 'No',
    },
    {
      label: 'Awareness before thank-you',
      value: assessment.awarenessSlideNumbers?.length
        ? assessment.isAwarenessSlidesBeforeThankYou
          ? 'Yes'
          : 'No'
        : 'None',
    },
    {
      label: 'Awareness slides',
      value: assessment.awarenessSlideNumbers?.length
        ? assessment.awarenessSlideNumbers.join(', ')
        : 'None',
    },
    {
      label: 'Blank slides',
      value: assessment.blankSlideNumbers?.length
        ? assessment.blankSlideNumbers.join(', ')
        : 'None',
    },
    {
      label: 'Slides missing PW logo',
      value: assessment.slidesMissingPwLogo?.length
        ? assessment.slidesMissingPwLogo.join(', ')
        : 'None',
    },
    {
      label: 'Faculty on cover',
      value: isPsFoundationCenter(center)
        ? 'Not applicable'
        : assessment.coverSlideNumber == null
          ? '—'
          : assessment.hasFacultyProfileOnCoverSlide
            ? 'Yes'
            : 'No',
    },
  ];

  return rows;
}

export function formatNotesExtractionRows(
  fileDetails?: Pick<
    NotesFileDetails,
    'chapterName' | 'lectureNo' | 'subtopics'
  >,
): Array<{ label: string; value: string }> {
  const subtopicList = fileDetails?.subtopics
    ?.map((item) => item.trim())
    .filter(Boolean);

  return [
    {
      label: 'Chapter name',
      value: fileDetails?.chapterName?.trim() || 'Not found',
    },
    {
      label: 'Lecture number',
      value: fileDetails?.lectureNo?.trim() || 'Not found',
    },
    {
      label: 'Subtopics',
      value: subtopicList?.length ? subtopicList.join(', ') : 'Not found',
    },
  ];
}

const SCHEDULE_SYNC_STATUS_LABELS: Record<string, string> = {
  done: 'Done',
  skipped: 'Skipped',
  failed: 'Failed',
};

function formatScheduleSyncAppliedFlag(value?: boolean): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '—';
}

export function formatScheduleSyncStatus(status?: string): string {
  const normalized = status?.trim();
  if (!normalized) return 'Pending';
  return (
    SCHEDULE_SYNC_STATUS_LABELS[normalized] ?? normalized.replace(/_/g, ' ')
  );
}

export function formatScheduleSyncRows(
  fileDetails?: Pick<
    NotesFileDetails,
    | 'scheduleSyncStatus'
    | 'scheduleSyncApplied'
    | 'scheduleSyncFailureReason'
    | 'scheduleSyncAt'
  >,
): Array<{ label: string; value: string }> {
  const rows: Array<{ label: string; value: string }> = [
    {
      label: 'Status',
      value: formatScheduleSyncStatus(fileDetails?.scheduleSyncStatus),
    },
  ];

  const applied = fileDetails?.scheduleSyncApplied;
  if (applied) {
    rows.push(
      {
        label: 'Notes PDF attached',
        value: formatScheduleSyncAppliedFlag(applied.notesPdf),
      },
      {
        label: 'DPP quiz attached',
        value: formatScheduleSyncAppliedFlag(applied.dppQuiz),
      },
      {
        label: 'DPP PDF attached',
        value: formatScheduleSyncAppliedFlag(applied.dppPdf),
      },
      {
        label: 'Schedule title updated',
        value: formatScheduleSyncAppliedFlag(applied.scheduleTitle),
      },
    );
  }

  if (fileDetails?.scheduleSyncFailureReason?.trim()) {
    rows.push({
      label: 'Failure reason',
      value: fileDetails.scheduleSyncFailureReason.trim(),
    });
  }

  if (fileDetails?.scheduleSyncAt) {
    const syncedAt = new Date(fileDetails.scheduleSyncAt);
    rows.push({
      label: 'Synced at',
      value: Number.isNaN(syncedAt.getTime())
        ? fileDetails.scheduleSyncAt
        : syncedAt.toLocaleString(),
    });
  }

  return rows;
}

export function formatChapterTagSyncRows(
  fileDetails?: Pick<
    NotesFileDetails,
    'chapterTagSyncStatus' | 'chapterTagSyncReason' | 'chapterTagSyncAt'
  >,
): Array<{ label: string; value: string }> {
  if (!fileDetails?.chapterTagSyncStatus?.trim()) {
    return [];
  }

  const rows: Array<{ label: string; value: string }> = [
    {
      label: 'Status',
      value: formatScheduleSyncStatus(fileDetails.chapterTagSyncStatus),
    },
  ];

  if (fileDetails.chapterTagSyncReason?.trim()) {
    rows.push({
      label: 'Reason',
      value: fileDetails.chapterTagSyncReason.trim(),
    });
  }

  if (fileDetails.chapterTagSyncAt) {
    const syncedAt = new Date(fileDetails.chapterTagSyncAt);
    rows.push({
      label: 'Synced at',
      value: Number.isNaN(syncedAt.getTime())
        ? fileDetails.chapterTagSyncAt
        : syncedAt.toLocaleString(),
    });
  }

  return rows;
}

const DPP_STATUS_LABELS: Record<string, string> = {
  resolved: 'Resolved',
  already_attached: 'Already attached',
  lecture_not_first: 'Skipped (not lecture 1)',
  chapter_not_found: 'Chapter not found',
  topic_tag_missing: 'Schedule tag missing',
  topic_not_found: 'Topic not found',
  lecture_number_missing: 'Lecture number missing',
  exercise_not_found: 'Exercise not found',
  batch_subject_missing: 'Batch subject missing',
  homework_not_found: 'Homework not found',
  error: 'Error',
};

function formatDppBranchStatus(status: string, reason?: string): string {
  const label = DPP_STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
  if (reason?.trim()) {
    return `${label} — ${reason.trim()}`;
  }
  return label;
}

export function formatDppIdentificationRows(
  dpp?: DppFinderResult,
): Array<{ label: string; value: string }> {
  if (!dpp) return [];

  const rows: Array<{ label: string; value: string }> = [
    {
      label: 'Quiz status',
      value: formatDppBranchStatus(dpp.quiz.status, dpp.quiz.reason),
    },
  ];

  if (dpp.quiz.chapter?.name) {
    rows.push({ label: 'Quiz chapter', value: dpp.quiz.chapter.name });
  }
  if (dpp.quiz.topic?.name) {
    rows.push({ label: 'Quiz topic', value: dpp.quiz.topic.name });
  }
  if (dpp.quiz.exercise?.title) {
    rows.push({ label: 'Quiz exercise', value: dpp.quiz.exercise.title });
  }
  if (dpp.quiz.exerciseId) {
    rows.push({ label: 'Quiz exercise id', value: dpp.quiz.exerciseId });
  }

  rows.push({
    label: 'PDF status',
    value: formatDppBranchStatus(dpp.pdf.status, dpp.pdf.reason),
  });

  if (dpp.pdf.homework?.name) {
    rows.push({ label: 'PDF homework', value: dpp.pdf.homework.name });
  }
  if (dpp.pdf.homeworkId) {
    rows.push({ label: 'PDF homework id', value: dpp.pdf.homeworkId });
  }

  return rows;
}

const DPP_OK_STATUSES = new Set([
  'resolved',
  'already_attached',
  'lecture_not_first',
]);

export function dppIdentificationHasIssues(dpp?: DppFinderResult): boolean {
  if (!dpp) return false;
  return buildDppIdentificationIssues(dpp).length > 0;
}

export function buildDppIdentificationIssues(
  dpp: DppFinderResult,
): DppIdentificationIssue[] {
  const issues: DppIdentificationIssue[] = [];

  if (!DPP_OK_STATUSES.has(dpp.quiz.status)) {
    issues.push({
      id: 'quiz',
      message: `Quiz: ${formatDppBranchStatus(dpp.quiz.status, dpp.quiz.reason)}`,
    });
  }

  if (!DPP_OK_STATUSES.has(dpp.pdf.status)) {
    issues.push({
      id: 'pdf',
      message: `PDF: ${formatDppBranchStatus(dpp.pdf.status, dpp.pdf.reason)}`,
    });
  }

  return issues;
}

export function summarizeDppIdentification(
  dpp?: DppFinderResult,
): DppIdentificationSummary {
  if (!dpp) {
    return {
      issueCount: 0,
      issues: [],
      statusLabel: 'DPP identification not available',
      statusTone: 'ok',
    };
  }

  const issues = buildDppIdentificationIssues(dpp);

  if (issues.length === 0) {
    return {
      issueCount: 0,
      issues,
      statusLabel: 'DPP identification OK',
      statusTone: 'ok',
    };
  }

  return {
    issueCount: issues.length,
    issues,
    statusLabel: `${issues.length} DPP issue${issues.length === 1 ? '' : 's'}`,
    statusTone: 'warning',
  };
}

export function summarizeDppIdentificationTooltip(
  dpp?: DppFinderResult,
): string | undefined {
  if (!dpp) return undefined;

  const quizLabel = DPP_STATUS_LABELS[dpp.quiz.status] ?? dpp.quiz.status;
  const pdfLabel = DPP_STATUS_LABELS[dpp.pdf.status] ?? dpp.pdf.status;
  return `Quiz: ${quizLabel} · PDF: ${pdfLabel}`;
}
